import { useId } from "react";

import { MAINTENANCE_MARGIN_RATE, liquidationPriceFor } from "@strat/engine";
import type { MarketType, TradeDirection } from "@strat/format";

/** Common Binance USD-M steps, plus 1 for holding an unleveraged position. */
export const LEVERAGE_STEPS = [1, 2, 3, 5, 10, 20, 50, 100];

/** Binance's ceiling on USD-M perpetuals, and the Optimize sweep's. */
export const MAX_LEVERAGE = 125;

/** How far price can move against a position before its margin is gone, in percent. */
export function liquidationMovePct(leverage: number): number {
  return Math.abs((1 / leverage - MAINTENANCE_MARGIN_RATE) * 100);
}

type Tone = "calm" | "moderate" | "high" | "extreme";

/**
 * A plain word for how much a leverage multiplies risk.
 *
 * The cut-offs follow the liquidation distance rather than taste: at 2× a
 * position survives a 49% move, at 5× a 19% one (a bad week for most coins),
 * at 20× a 4.5% one (an ordinary day), and past that a single candle decides.
 */
function toneOf(leverage: number): { tone: Tone; label: string } {
  if (leverage <= 2) return { tone: "calm", label: leverage === 1 ? "No leverage" : "Low" };
  if (leverage <= 5) return { tone: "moderate", label: "Moderate" };
  if (leverage <= 20) return { tone: "high", label: "High" };
  return { tone: "extreme", label: "Extreme" };
}

const fmtMoney = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtPct = (n: number) => `${n.toLocaleString("en-US", { maximumFractionDigits: n < 10 ? 1 : 0 })}%`;

export interface LeverageControlProps {
  value: number;
  onChange: (leverage: number) => void;
  direction: TradeDirection;
  marketType: MarketType;
  /** The highest leverage offered here — 125 for research, the live cap for a bot. */
  max?: number;
  /** Quick picks; those above `max` are left out. */
  steps?: number[];
  /** Margin committed per trade, in the quote currency, when the sizing makes it knowable. */
  margin?: number | null;
  quote?: string;
  /** The stop-loss, as a price move in percent, to compare against the liquidation distance. */
  stopLossPct?: number;
  /** Offered on a spot market: moves the run to the same coin's futures market. */
  onSwitchToFutures?: () => void;
  /** What `onSwitchToFutures` switches to, e.g. "BTCUSDT futures". */
  futuresLabel?: string;
  /** Said under the control when there is no futures market to switch to. */
  noFuturesHint?: string;
  /** On spot: true when this place forces 1× (a bot), false when it only warns (a backtest). */
  spotForcesOne?: boolean;
  /** An extra line under the control, e.g. "Paper bots run up to 10×". */
  note?: string;
}

/**
 * Leverage, with what it means said next to it.
 *
 * The number on its own is the least useful part. What decides whether a
 * leverage is sensible is how far price may move before the position is
 * liquidated, how big the position really is, and whether the stop-loss
 * still fires before the exchange does — so all three are shown, live, in
 * the same place the number is picked.
 *
 * On spot there is no leverage to pick, but the control still shows — it is
 * where somebody looking for leverage will look — and says how to get it.
 */
export function LeverageControl({
  value,
  onChange,
  direction,
  marketType,
  max = MAX_LEVERAGE,
  steps = LEVERAGE_STEPS,
  margin,
  quote = "USDT",
  stopLossPct,
  onSwitchToFutures,
  futuresLabel,
  noFuturesHint,
  spotForcesOne = false,
  note,
}: LeverageControlProps) {
  const uid = useId();
  const leverage = Math.max(1, value || 1);

  if (marketType !== "futures") {
    const leveragedOnSpot = leverage > 1 && !spotForcesOne;
    return (
      <div
        className={`lev lev--spot${leveragedOnSpot ? " lev--warn" : ""}`}
        role="group"
        aria-labelledby={`${uid}-label`}
      >
        <div className="lev__head">
          <span className="field__label" id={`${uid}-label`}>
            Leverage
          </span>
          <span className="lev__value">{leveragedOnSpot ? `${leverage}×` : "1×"}</span>
        </div>
        <p className="lev__text">
          {leveragedOnSpot ? (
            <>
              <strong>This strategy is set to {leverage}×, but spot has no leverage.</strong> The backtest
              still models {leverage}× (wins and losses {leverage} times larger, liquidations included), and a
              paper bot would refuse it on spot.
            </>
          ) : spotForcesOne && leverage > 1 ? (
            <>
              The strategy is set to {leverage}×. Spot has no leverage, so this bot runs at 1×; pick futures
              to keep {leverage}×.
            </>
          ) : (
            <>Spot trades with your own cash only. Leverage and funding need a futures (perpetual) market.</>
          )}
        </p>
        <div className="row lev__actions">
          {onSwitchToFutures ? (
            <button type="button" className="btn btn--sm btn--primary" onClick={onSwitchToFutures}>
              Switch to {futuresLabel ?? "futures"}
            </button>
          ) : (
            noFuturesHint && <span className="small muted">{noFuturesHint}</span>
          )}
          {leveragedOnSpot && (
            <button type="button" className="btn btn--sm" onClick={() => onChange(1)}>
              Use 1× on spot
            </button>
          )}
        </div>
      </div>
    );
  }

  // The value in use gets its own button when it is not a preset — a 4× strategy shows 4×.
  const shown = [...new Set([...steps.filter((s) => s <= max), leverage])].sort((a, b) => a - b);
  const move = liquidationMovePct(leverage);
  const liqAt = liquidationPriceFor(direction, 100, leverage);
  const { tone, label } = toneOf(leverage);
  // Fill on a log scale: 1× → 0, max → 100%. Linear would put 2×–10× in the first sliver.
  const fill = max > 1 ? (Math.log(leverage) / Math.log(max)) * 100 : 0;
  const stopLate = leverage > 1 && stopLossPct !== undefined && stopLossPct >= move;
  const noStop = leverage > 1 && stopLossPct === undefined;

  return (
    <div className={`lev lev--${tone}`} role="group" aria-labelledby={`${uid}-label`}>
      <div className="lev__head">
        <span className="field__label" id={`${uid}-label`}>
          Leverage
        </span>
        <span className={`lev__tone lev__tone--${tone}`}>{label}</span>
      </div>

      <div className="lev__picker">
        <div className="segmented segmented--wrap lev__steps" role="radiogroup" aria-label="Leverage presets">
          {shown.map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={s === leverage}
              className={s === leverage ? "is-active" : undefined}
              onClick={() => onChange(s)}
            >
              {s}×
            </button>
          ))}
        </div>
        <div className="input-suffix lev__custom">
          <input
            type="number"
            min={1}
            max={max}
            step={1}
            aria-label="Leverage (custom)"
            value={leverage}
            onChange={(e) => {
              const n = Math.round(Number(e.target.value));
              if (Number.isFinite(n)) onChange(Math.min(max, Math.max(1, n)));
            }}
          />
          <span>×</span>
        </div>
      </div>

      <div className="lev__meter" aria-hidden="true">
        <span style={{ width: `${Math.max(2, fill)}%` }} />
      </div>

      <dl className="lev__facts">
        <div>
          <dt>Liquidated after</dt>
          <dd>
            {leverage === 1 ? (
              "never — no borrowed money"
            ) : (
              <>
                a {fmtPct(move)} move {direction === "long" ? "down" : "up"}{" "}
                <span className="muted">(entry 100 → {liqAt.toFixed(1)})</span>
              </>
            )}
          </dd>
        </div>
        {margin != null && margin > 0 && (
          <div>
            <dt>Position per trade</dt>
            <dd>
              {fmtMoney(margin * leverage)} {quote}{" "}
              {leverage > 1 && (
                <span className="muted">
                  ({fmtMoney(margin)} margin × {leverage})
                </span>
              )}
            </dd>
          </div>
        )}
        {stopLossPct !== undefined && leverage > 1 && (
          <div>
            <dt>A stop-loss hit costs</dt>
            <dd>
              {stopLate ? (
                "the whole margin — liquidation comes first"
              ) : (
                <>
                  ≈ {fmtPct(stopLossPct * leverage)} of the margin{" "}
                  <span className="muted">
                    ({stopLossPct}% × {leverage})
                  </span>
                </>
              )}
            </dd>
          </div>
        )}
      </dl>

      {stopLate && (
        <p className="lev__alert" role="alert">
          Your {stopLossPct}% stop-loss is past the liquidation point ({fmtPct(move)}): the position is
          liquidated before the stop can fire. Tighten the stop or lower the leverage.
        </p>
      )}
      {noStop && (
        <p className="lev__alert" role="alert">
          No stop-loss: at {leverage}× only liquidation limits a losing trade.
        </p>
      )}
      {note && <p className="small muted lev__note">{note}</p>}
    </div>
  );
}
